import { justDragged } from './webledgerSparkline.js';

/**
 * Inspecting one day on a page of balance charts — the gestures, shared by
 * the accounts page and the wallets page so they cannot drift apart.
 *
 * A click on a chart picks the day under it. The arrow keys, or a swipe on a
 * chart on a touch screen, step to the previous or next day anything
 * changed. A click or tap anywhere that is not a chart leaves. What a day
 * MEANS — which totals change, what the line is drawn through — is the
 * page's; this only says when.
 */
export interface InspectGestures {
  /** Whether a day is being inspected right now. */
  active(): boolean;
  /** Pick the day under `clientX` on this chart. */
  pick(box: HTMLElement, clientX: number): void;
  step(direction: 1 | -1): void;
  exit(): void;
  /** Pin the tooltip where it is, or let it go — the P key. */
  pin?(): void;
  /** The charts that can be clicked, e.g. `.wl-spark[data-acct]`. */
  chartSelector: string;
  /**
   * Swallow the click that leaves when it lands inside the list — where the
   * charts sit in buttons, so the click that only meant "stop inspecting"
   * would otherwise also open what it landed on.
   */
  swallowLeavingClick?: boolean;
}

export function wireInspectGestures(container: HTMLElement, g: InspectGestures): void {
  const onChart = (t: EventTarget | null): HTMLElement | null =>
    (t instanceof Element ? t.closest<HTMLElement>(g.chartSelector) : null);

  // Capture, so a chart inside a button picks a day instead of pressing it.
  container.addEventListener('click', (event) => {
    const box = onChart(event.target);
    if (!box || justDragged(container)) return;
    event.stopPropagation();
    event.preventDefault();
    g.pick(box, event.clientX);
  }, true);

  document.addEventListener('click', (event) => {
    if (!g.active() || onChart(event.target)) return;
    // Asked before leaving: leaving redraws the list, and the click's
    // target is then no longer in it — so the check after would always say
    // "outside", and the click would go on to open the row it landed on.
    const inside = container.contains(event.target as Node);
    g.exit();
    if (g.swallowLeavingClick && inside) {
      event.stopPropagation();
      event.preventDefault();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const isPin = (event.key === 'p' || event.key === 'P') && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (!g.active() || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && !(isPin && g.pin))) return;
    // Not while somebody is typing, or inside a picker or a dialog.
    // (`document` itself is the target when nothing has focus, and has no `closest`.)
    const t = event.target;
    if (t instanceof Element && t.closest('input, textarea, select, [contenteditable], .modal.show, dialog[open], .wl-apk')) return;
    event.preventDefault();
    if (isPin) { g.pin!(); return; }
    g.step(event.key === 'ArrowRight' ? 1 : -1);
  });

  /*
   * A swipe on a chart steps. Touch events, not pointer events: on a phone
   * the browser claims a sideways move for its own scrolling the moment it
   * starts, cancels the pointer, and the swipe never ends where a pointer
   * handler can see it — reported from a real device as the page scrolling
   * sideways instead. Cancelling `touchmove` is the one thing every mobile
   * browser honours, and only a sideways move that began on a chart while
   * inspecting is cancelled; anything else scrolls as it always has.
   */
  let start: { x: number; y: number } | undefined;
  let sideways = false;
  container.addEventListener('touchstart', (event) => {
    start = undefined;
    sideways = false;
    if (!g.active() || event.touches.length !== 1 || !onChart(event.target)) return;
    start = { x: event.touches[0]!.clientX, y: event.touches[0]!.clientY };
  }, { passive: true });
  container.addEventListener('touchmove', (event) => {
    if (!start) return;
    const t = event.touches[0]!;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (!sideways && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) sideways = true;
    if (sideways) event.preventDefault();
  }, { passive: false });
  container.addEventListener('touchend', (event) => {
    const began = start;
    start = undefined;
    if (!began || !sideways) return;
    const t = event.changedTouches[0]!;
    const dx = t.clientX - began.x;
    if (Math.abs(dx) < 40) return;
    // The tap that a swipe also is must not pick a day.
    event.preventDefault();
    // Left is forward, as in every list that pages by swiping.
    g.step(dx < 0 ? 1 : -1);
  });
}
